import React, { useEffect, useMemo, useRef, useState } from "react";
import { colors } from "../../constants/theme";
import type { Item, SearchFilter } from "../../types";

const FIELD_LABELS = [
  { key: "name", label: "Name", path: (it: Item) => it.filename ?? "" },
  { key: "artist", label: "Artist", path: (it: Item) => it.meta?.artist ?? "" },
  { key: "title", label: "Title", path: (it: Item) => it.meta?.title ?? "" },
  { key: "location", label: "Location", path: (it: Item) => it.location ?? "" },
  { key: "deviceid", label: "DeviceId", path: (it: Item) => it.deviceId ?? "" },
  { key: "status", label: "Status", path: (it: Item) => String(it.status ?? "") },
];

interface SearchBoxProps {
  value: string;
  onChange: (v: string) => void;
  onApply: (f: SearchFilter) => void;
  activeField?: string;
}

export const SearchBox: React.FC<SearchBoxProps> = ({ value, onChange, onApply, activeField }) => {
  const [open, setOpen] = useState(false);
  const [hoverIdx, setHoverIdx] = useState(0);
  const [typedSinceFocus, setTypedSinceFocus] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  const suggestions = useMemo(() => {
    const q = value.trim();
    const safe = q || "";
    return FIELD_LABELS.map((f) => ({ key: f.key, label: `${f.label} contains "${safe}"` }));
  }, [value]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (!open) return;
      if (e.key === "ArrowDown") setHoverIdx((i) => (i + 1) % suggestions.length);
      else if (e.key === "ArrowUp") setHoverIdx((i) => (i - 1 + suggestions.length) % suggestions.length);
      else if (e.key === "Enter") {
        const chosen = suggestions[hoverIdx];
        if (chosen) onApply({ field: chosen.key, query: value });
        setOpen(false);
        setTypedSinceFocus(false);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, hoverIdx, suggestions, onApply, value]);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) { setOpen(false); setTypedSinceFocus(false); }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const activeLabel = FIELD_LABELS.find((f) => f.key === activeField)?.label;
  const clearValue = () => { onChange(""); setOpen(false); setTypedSinceFocus(false); };

  return (
    <div ref={ref} style={{ position: "relative", width: 360, minWidth: 280, maxWidth: 500, flexShrink: 1 }}>
      <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
        {activeLabel && (
          <div style={{ position: "absolute", left: 8, display: "flex", alignItems: "center", background: "#f1f1f1", color: colors.text, fontSize: 12, borderRadius: 6, padding: "2px 6px", border: `1px solid ${colors.border}`, fontWeight: 600 }}>{activeLabel}</div>
        )}
        <input
          value={value}
          onChange={(e) => { const v = e.target.value; onChange(v); setHoverIdx(0); setTypedSinceFocus(true); setOpen(v.length > 0); }}
          onFocus={() => { setOpen(false); setTypedSinceFocus(false); }}
          placeholder="Search…"
          style={{ width: "100%", padding: `10px ${value ? 28 : 12}px 10px ${activeLabel ? 70 : 12}px`, borderRadius: 20, border: `1px solid ${colors.border}`, background: "#fff", color: colors.text, boxShadow: open ? colors.shadow : undefined }}
        />
        {value && (
          <button onClick={clearValue} style={{ position: "absolute", right: 8, width: 18, height: 18, borderRadius: "50%", border: "none", background: "#eee", color: colors.muted, fontSize: 12, lineHeight: 1, cursor: "pointer" }} aria-label="Clear search">×</button>
        )}
      </div>
      {open && typedSinceFocus && (
        <div style={{ position: "absolute", top: 44, left: 0, right: 0, background: "#fff", border: `1px solid ${colors.border}`, borderRadius: 10, boxShadow: colors.shadow, overflow: "hidden", zIndex: 10 }}>
          {suggestions.map((s, idx) => (
            <div key={s.key} onMouseEnter={() => setHoverIdx(idx)} onMouseDown={(e) => { e.preventDefault(); onApply({ field: s.key, query: value }); setOpen(false); setTypedSinceFocus(false); }} style={{ padding: "10px 12px", background: idx === hoverIdx ? "#f2f6ff" : "#fff", color: colors.text, cursor: "pointer", display: "flex", justifyContent: "space-between" }}>
              <span>{s.label}</span>
              <span style={{ color: colors.muted, fontSize: 12 }}>{s.key}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

